import React, {useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {elevation} from "@pg-design/helpers-css-strings";
import {CloseIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {AuthCard} from "../../../auth/components/AuthCard";
import {LeadSidebar} from "../../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../../lead/components/lead_sidebar/SidebarTestContainer";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {Country} from "../../../region/types/Country";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {gtmEventConstructionPhotosRegister} from "../../../tracking/google_tag_manager/gtm_event_construction_photos_register";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {generateHash} from "../../../utils/generate_hash";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryPictures?: IConstructionGalleryResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolderModal = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, modalState, onModalClose, offerBuildings} = props;
    const storeHash = useMemo(() => generateHash(), []);
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = props.modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAuthenticated = useIsAuthenticated();
    const [showAuthCard, setShowAuthCard] = useState(!isAuthenticated);
    const sourceSection = modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY;
    const showSidebar = (isClassicView && isPropertyForSale) || (!property && isClassicView);
    const isAbroad = offer.region.country !== Country.POLAND;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    const handlers = {
        onClose: () => {
            onModalClose && onModalClose();
        },
        onPlanButtonClick: () => {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
        },
        onAuthSuccess: () => {
            setShowAuthCard(false);
        },
        onAuthCardClose: () => {
            setShowAuthCard(false);
        }
    };

    const lastGalleryTile = props.constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={handlers.onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={handlers.onPlanButtonClick} />}
                            className={css({
                                p: 3,
                                md: {
                                    p: 0
                                }
                            })}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        shouldShowVideo
                        offer={offer}
                        offerGallery={props.galleryPictures ?? []}
                        propertyGallery={props.propertyGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                if (isAuthenticated && props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0) {
                    return (
                        <OfferGalleryModalContent key="offerConstructionGallery" offer={offer} constructionGallery={props.constructionGalleryPictures ?? []} />
                    );
                } else if (!isAuthenticated && props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0) {
                    return (
                        <>
                            <Text
                                variant="headline_4"
                                as="p"
                                color="white"
                                align="center"
                                mb={3}
                                className={noAuthTextStyle}
                                onClick={() => setShowAuthCard(true)}
                            >
                                <Text as="span" variant="headline_4" strong css={{color: "var(--colors-primary)"}}>
                                    Zaloguj się
                                </Text>{" "}
                                aby zobaczyć zdjęcia z budowy.
                            </Text>
                            <Modal
                                isOpen={showAuthCard}
                                onModalClose={handlers.onAuthCardClose}
                                className={modalStyle}
                                closeButton={(closeModal) => <CloseIcon size="2" wrapperSize="4.8" onClick={closeModal} className={closeButtonStyle} />}
                            >
                                <div className={authCardStyle}>
                                    <Modal.CloseButton placement="right" />
                                    <AuthCard
                                        key="auth"
                                        onAuthSuccess={handlers.onAuthSuccess}
                                        userSource={UserSource.REGISTER_CONSTRUCTION_PHOTOS}
                                        gtmEventData={() => gtmEventConstructionPhotosRegister()}
                                        modalTitle={
                                            <>
                                                Załóż konto, aby zobaczyć <br /> zdjęcia z budowy
                                            </>
                                        }
                                    />
                                </div>
                            </Modal>
                        </>
                    );
                } else if (!props.constructionGalleryPictures || props.constructionGalleryPictures.length === 0) {
                    return <ConstructionGalleryPlaceholder />;
                }
                break;
            case OfferModalActiveTab.MAP:
                return (
                    <OfferMapModalContent
                        offer={{
                            ...offer,
                            geo_area: {
                                coordinates: {
                                    coordinates: offer.geo_area.coordinates
                                }
                            }
                        }}
                        offerBuildings={offerBuildings}
                        property={property}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <RPModal
                {...props}
                className={cx(galleryModalStyle, isMapsTabOpen ? css({p: "0"}) : css({p: "0.5rem"}))}
                isOpen={props.isOpen}
                onModalClose={handlers.onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" className={modalCloseIconHolder} />}
            >
                <div className={galleryModalImmediateWrapper}>
                    <div className={cx(modalHeaderStyle, isMapsTabOpen && mapsTabOpenHeaderStyle)}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={props.modalState || undefined}
                        />
                    </div>

                    <div className={galleryModalContent}>
                        <div className={galleryModalMainContent}>
                            {renderModalContent()}

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ContactBar
                                    className={cx(contactBarStyle, isMapsTabOpen && contactBarWithMapsTabOpenStyle)}
                                    salesOffices={offer.sales_offices}
                                    vendor={offer.vendor}
                                    trackVendor={trackedData.vendor}
                                    trackOffer={trackedData.offer}
                                    trackProperty={trackedData.property}
                                    phoneClip={offer.configuration.phone_clip}
                                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                                    backgroundColor="#20212d"
                                    source={props.source}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    offer={offer}
                                    propertyId={property?.id}
                                    propertyPrice={property?.price}
                                    buttonsSize="x-small"
                                    modalState={modalState}
                                />
                            )}
                        </div>

                        <div className={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" className={offerInfoTextStyle} mb={2}>
                                <span className="bt">{offer.name}</span>
                                {props.property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${props.property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" className={cx(offerInfoTextStyle, "bt")} mb={2}>
                                {offer.address}
                            </Text>

                            {showSidebar && props.property && !isAbroad ? (
                                <SidebarTestContainer>
                                    {[
                                        () => (
                                            <ApplicationBox
                                                officeData={offer.sales_offices[0]}
                                                storeHash={storeHash}
                                                source={props.source}
                                                sourceSection={sourceSection}
                                                vendorId={offer.vendor.id}
                                                offerId={offer.id}
                                                property={property}
                                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                                optionalVendorInfoData={offer.vendor}
                                                optionalOfferPhoneData={offer}
                                                formButtonText="Zapytaj dewelopera"
                                                sidebarBg="dark"
                                                modalState={modalState}
                                                offerStatusStatus={offer?.status?.status}
                                            />
                                        ),
                                        () => (
                                            <LeadSidebar
                                                vendor={offer.vendor}
                                                offerId={offer.id}
                                                propertyId={property?.id}
                                                source={props.source}
                                                sourceSection={sourceSection}
                                                sidebarBg="dark"
                                                blockScrolling
                                            />
                                        )
                                    ]}
                                </SidebarTestContainer>
                            ) : (
                                <ApplicationBox
                                    officeData={offer.sales_offices[0]}
                                    storeHash={storeHash}
                                    source={props.source}
                                    sourceSection={sourceSection}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    property={property}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    optionalVendorInfoData={offer.vendor}
                                    optionalOfferPhoneData={offer}
                                    formButtonText="Zapytaj dewelopera"
                                    sidebarBg="dark"
                                    modalState={modalState}
                                    offerStatusStatus={offer?.status?.status}
                                />
                            )}
                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton
                                    onPlanButtonClick={handlers.onPlanButtonClick}
                                    propertyPlan={property.plan}
                                    modalState={modalState}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </RPModal>
        </>
    );
};

const galleryModalImmediateWrapper = css({
    display: "flex",
    flex: "100%",
    flexDirection: "column",
    overflow: "hidden"
});

const mapsTabOpenHeaderStyle = css({
    md: {
        pb: 0,
        paddingLeft: 0,
        paddingRight: 0
    }
});

const galleryModalContent = css({
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "row",
    overflow: "auto",
    position: "relative",
    md: {
        overflow: "hidden"
    }
});

const galleryModalMainContent = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",
    md: {
        overflow: "auto",
        minHeight: "auto"
    }
});

const offerInfoTextStyle = css({
    color: "#fff"
});

const modalCloseIconHolder = css({
    position: "absolute",
    right: "1rem",
    top: "1rem",
    cursor: "pointer",
    md: {
        right: "3rem",
        top: "3rem"
    }
});

const galleryModalStyle = css({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: 0,
    md: {
        padding: 3
    }
});

const modalHeaderStyle = css({
    flex: "1 0 auto",
    color: "white",
    padding: "2rem 0 2.4rem 0",
    display: "flex",
    flexDirection: "column",
    md: {
        padding: "2rem 5rem 2.8rem 3rem",
        flexDirection: "row",
        flexWrap: "wrap"
    }
});

const galleryModalFormHolder = css({
    flexGrow: "0",
    flexShrink: "0",
    display: "none",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        pl: 3,
        overflow: "auto"
    }
});

const contactBarStyle = css({
    position: "sticky !important", // ugly `!important` needed - probably a problem with merging emotion with panda
    bottom: 0,
    left: 0,
    p: 1,
    mt: "auto"
});

const contactBarWithMapsTabOpenStyle = css({
    paddingLeft: 2,
    paddingRight: 2
});

const authCardStyle = css({
    display: "flex",
    justifyContent: "center"
});

const modalStyle = css({
    bottom: -1,
    borderRadius: 2,
    overflow: "unset",
    width: "100%",
    md: {
        bottom: "unset",
        maxWidth: "500px"
    }
});

const closeButtonStyle = css({
    cursor: "pointer",
    position: "absolute",
    top: -7,
    boxShadow: elevation(),
    md: {
        top: -3,
        right: -3,
        transform: "unset",
        boxShadow: elevation()
    }
});

const noAuthTextStyle = css({
    width: "100%",
    mt: 2,
    cursor: "pointer",
    maxHeight: "max-content"
});
import React, {useMemo, useState} from "react";
import {css, cx} from "@panda/css";

import {elevation} from "@pg-design/helpers-css-strings";
import {CloseIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {RPModal} from "../../../atoms/RPModal";
import {AuthCard} from "../../../auth/components/AuthCard";
import {LeadSidebar} from "../../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../../lead/components/lead_sidebar/SidebarTestContainer";
import {PropertyPlanDisplay} from "../../../property/components/PropertyPlanDisplay";
import {PropertyPlanModalContent} from "../../../property/components/PropertyPlanModalContent";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {Country} from "../../../region/types/Country";
import {ShowPropertyPlanView, showPropertyPlanView} from "../../../tracking/algolytics_hits/click_actions/show_property_plan_click";
import {gtmEventConstructionPhotosRegister} from "../../../tracking/google_tag_manager/gtm_event_construction_photos_register";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {generateHash} from "../../../utils/generate_hash";
import {GalleryModalTabs} from "../../atoms/GalleryModalTabs";
import {OfferMapModalContent} from "../../detail/components/OfferMapModalContent";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {OfferDisplayType} from "../../helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../helpers/OfferType";
import {IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {ConstructionGalleryPlaceholder} from "./ConstructionGalleryPlaceholder";
import {GalleryHolderPropertyPlanButton} from "./GalleryHolderPropertyPlanButton";
import {OfferConstructionGalleryTile} from "./OfferConstructionGalleryTile";
import {OfferGalleryModalContent} from "./OfferGalleryModalContent";

interface IProps {
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    property?: IPropertyDetail;
    galleryPictures?: IOfferGallery | null;
    propertyGalleryPictures?: IPropertyGallery | null;
    constructionGalleryPictures?: IConstructionGalleryResponse | null;
    offerBuildings?: IOfferBuilding[];
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    onModalClose?: () => void;
    isOpen: boolean;
}

export const GalleryHolderModal = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, modalState, onModalClose, offerBuildings} = props;
    const storeHash = useMemo(() => generateHash(), []);
    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isLimited && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property?.for_sale;
    const isMapsTabOpen = props.modalState === OfferModalActiveTab.MAP;
    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAuthenticated = useIsAuthenticated();
    const [showAuthCard, setShowAuthCard] = useState(!isAuthenticated);
    const sourceSection = modalState === OfferModalActiveTab.MAP ? ApplicationSourceSection.MAP : ApplicationSourceSection.GALLERY;
    const showSidebar = (isClassicView && isPropertyForSale) || (!property && isClassicView);
    const isAbroad = offer.region.country !== Country.POLAND;

    const trackedData = {
        offer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        vendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        },
        property: property && {
            area: property.area,
            floor: property.floor,
            floors: property.floors,
            id: property.id,
            is_luxury: property.is_luxury,
            is_reserved: property.is_reserved,
            number: property.number,
            plan: property.plan,
            rooms: property.rooms
        }
    };

    const handlers = {
        onClose: () => {
            onModalClose && onModalClose();
        },
        onPlanButtonClick: () => {
            showPropertyPlanView(ShowPropertyPlanView.PLAN_IMAGE_DOWNLOAD, trackedData.offer, trackedData.vendor);
        },
        onAuthSuccess: () => {
            setShowAuthCard(false);
        },
        onAuthCardClose: () => {
            setShowAuthCard(false);
        }
    };

    const lastGalleryTile = props.constructionGalleryPictures?.length ? null : <OfferConstructionGalleryTile onButtonClick={handleGalleryModalChange} />;

    const renderModalContent = () => {
        switch (modalState) {
            case OfferModalActiveTab.PLAN:
                return (
                    property && (
                        <PropertyPlanDisplay
                            property={property}
                            vendor={offer.vendor}
                            onPlanDownloadClick={handlers.onPlanButtonClick}
                            legacyPlan={<PropertyPlanModalContent offerName={offer.name} property={property} onPlanButtonClick={handlers.onPlanButtonClick} />}
                            className={css({
                                p: 3,
                                md: {
                                    p: 0
                                }
                            })}
                        />
                    )
                );
            case OfferModalActiveTab.GALLERY:
                return (
                    <OfferGalleryModalContent
                        key="offerGallery"
                        shouldShowVideo
                        offer={offer}
                        offerGallery={props.galleryPictures ?? []}
                        propertyGallery={props.propertyGalleryPictures ?? []}
                        showMainImage
                        lastGalleryTile={lastGalleryTile}
                    />
                );
            case OfferModalActiveTab.CONSTRUCTION_GALLERY:
                if (isAuthenticated && props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0) {
                    return (
                        <OfferGalleryModalContent key="offerConstructionGallery" offer={offer} constructionGallery={props.constructionGalleryPictures ?? []} />
                    );
                } else if (!isAuthenticated && props.constructionGalleryPictures && props.constructionGalleryPictures.length > 0) {
                    return (
                        <>
                            <Text
                                variant="headline_4"
                                as="p"
                                color="white"
                                align="center"
                                mb={3}
                                className={noAuthTextStyle}
                                onClick={() => setShowAuthCard(true)}
                            >
                                <Text as="span" variant="headline_4" strong css={{color: "var(--colors-primary)"}}>
                                    Zaloguj się
                                </Text>{" "}
                                aby zobaczyć zdjęcia z budowy.
                            </Text>
                            <Modal
                                isOpen={showAuthCard}
                                onModalClose={handlers.onAuthCardClose}
                                className={modalStyle}
                                closeButton={(closeModal) => <CloseIcon size="2" wrapperSize="4.8" onClick={closeModal} className={closeButtonStyle} />}
                            >
                                <div className={authCardStyle}>
                                    <Modal.CloseButton placement="right" />
                                    <AuthCard
                                        key="auth"
                                        onAuthSuccess={handlers.onAuthSuccess}
                                        userSource={UserSource.REGISTER_CONSTRUCTION_PHOTOS}
                                        gtmEventData={() => gtmEventConstructionPhotosRegister()}
                                        modalTitle={
                                            <>
                                                Załóż konto, aby zobaczyć <br /> zdjęcia z budowy
                                            </>
                                        }
                                    />
                                </div>
                            </Modal>
                        </>
                    );
                } else if (!props.constructionGalleryPictures || props.constructionGalleryPictures.length === 0) {
                    return <ConstructionGalleryPlaceholder />;
                }
                break;
            case OfferModalActiveTab.MAP:
                return (
                    <OfferMapModalContent
                        offer={{
                            ...offer,
                            geo_area: {
                                coordinates: {
                                    coordinates: offer.geo_area.coordinates
                                }
                            }
                        }}
                        offerBuildings={offerBuildings}
                        property={property}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <RPModal
                {...props}
                className={cx(galleryModalStyle, isMapsTabOpen ? css({p: "0"}) : css({p: "0.5rem"}))}
                isOpen={props.isOpen}
                onModalClose={handlers.onClose}
                closeButton={(closeModal) => <CloseIcon onClick={closeModal} fill="#fff" size="2" className={modalCloseIconHolder} />}
            >
                <div className={galleryModalImmediateWrapper}>
                    <div className={cx(modalHeaderStyle, isMapsTabOpen && mapsTabOpenHeaderStyle)}>
                        <GalleryModalTabs
                            hasPlanImage={!isEmpty(property?.plan_image_pages)}
                            handleGalleryModalChange={handleGalleryModalChange}
                            modalState={props.modalState || undefined}
                        />
                    </div>

                    <div className={galleryModalContent}>
                        <div className={galleryModalMainContent}>
                            {renderModalContent()}

                            {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                                <ContactBar
                                    className={cx(contactBarStyle, isMapsTabOpen && contactBarWithMapsTabOpenStyle)}
                                    salesOffices={offer.sales_offices}
                                    vendor={offer.vendor}
                                    trackVendor={trackedData.vendor}
                                    trackOffer={trackedData.offer}
                                    trackProperty={trackedData.property}
                                    phoneClip={offer.configuration.phone_clip}
                                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                                    backgroundColor="#20212d"
                                    source={props.source}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    offer={offer}
                                    propertyId={property?.id}
                                    propertyPrice={property?.price}
                                    buttonsSize="x-small"
                                    modalState={modalState}
                                />
                            )}
                        </div>

                        <div className={galleryModalFormHolder}>
                            <Text variant="headline_4" as="h1" className={offerInfoTextStyle} mb={2}>
                                <span className="bt">{offer.name}</span>
                                {props.property && ` - ${isApartment ? "Apartament" : getOfferTypeNameCapital(offer.type)} - ${props.property.number}`}
                            </Text>
                            <Text variant="info_txt_1" as="h2" className={cx(offerInfoTextStyle, "bt")} mb={2}>
                                {offer.address}
                            </Text>

                            {showSidebar && props.property && !isAbroad ? (
                                <SidebarTestContainer>
                                    {[
                                        () => (
                                            <ApplicationBox
                                                officeData={offer.sales_offices[0]}
                                                storeHash={storeHash}
                                                source={props.source}
                                                sourceSection={sourceSection}
                                                vendorId={offer.vendor.id}
                                                offerId={offer.id}
                                                property={property}
                                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                                optionalVendorInfoData={offer.vendor}
                                                optionalOfferPhoneData={offer}
                                                formButtonText="Zapytaj dewelopera"
                                                sidebarBg="dark"
                                                modalState={modalState}
                                                offerStatusStatus={offer?.status?.status}
                                            />
                                        ),
                                        () => (
                                            <LeadSidebar
                                                vendor={offer.vendor}
                                                offerId={offer.id}
                                                propertyId={property?.id}
                                                source={props.source}
                                                sourceSection={sourceSection}
                                                sidebarBg="dark"
                                                blockScrolling
                                            />
                                        )
                                    ]}
                                </SidebarTestContainer>
                            ) : (
                                <ApplicationBox
                                    officeData={offer.sales_offices[0]}
                                    storeHash={storeHash}
                                    source={props.source}
                                    sourceSection={sourceSection}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    property={property}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    optionalVendorInfoData={offer.vendor}
                                    optionalOfferPhoneData={offer}
                                    formButtonText="Zapytaj dewelopera"
                                    sidebarBg="dark"
                                    modalState={modalState}
                                    offerStatusStatus={offer?.status?.status}
                                />
                            )}
                            {property && property.plan && (
                                <GalleryHolderPropertyPlanButton
                                    onPlanButtonClick={handlers.onPlanButtonClick}
                                    propertyPlan={property.plan}
                                    modalState={modalState}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </RPModal>
        </>
    );
};

const galleryModalImmediateWrapper = css({
    display: "flex",
    flex: "100%",
    flexDirection: "column",
    overflow: "hidden"
});

const mapsTabOpenHeaderStyle = css({
    md: {
        pb: 0,
        paddingLeft: 0,
        paddingRight: 0
    }
});

const galleryModalContent = css({
    flex: "1 1 100%",
    display: "flex",
    flexDirection: "row",
    overflow: "auto",
    position: "relative",
    md: {
        overflow: "hidden"
    }
});

const galleryModalMainContent = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",
    md: {
        overflow: "auto",
        minHeight: "auto"
    }
});

const offerInfoTextStyle = css({
    color: "#fff"
});

const modalCloseIconHolder = css({
    position: "absolute",
    right: "1rem",
    top: "1rem",
    cursor: "pointer",
    md: {
        right: "3rem",
        top: "3rem"
    }
});

const galleryModalStyle = css({
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--colors-secondary)",
    borderRadius: 0,
    md: {
        padding: 3
    }
});

const modalHeaderStyle = css({
    flex: "1 0 auto",
    color: "white",
    padding: "2rem 0 2.4rem 0",
    display: "flex",
    flexDirection: "column",
    md: {
        padding: "2rem 5rem 2.8rem 3rem",
        flexDirection: "row",
        flexWrap: "wrap"
    }
});

const galleryModalFormHolder = css({
    flexGrow: "0",
    flexShrink: "0",
    display: "none",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        pl: 3,
        overflow: "auto"
    }
});

const contactBarStyle = css({
    position: "sticky !important", // ugly `!important` needed - probably a problem with merging emotion with panda
    bottom: 0,
    left: 0,
    p: 1,
    mt: "auto"
});

const contactBarWithMapsTabOpenStyle = css({
    paddingLeft: 2,
    paddingRight: 2
});

const authCardStyle = css({
    display: "flex",
    justifyContent: "center"
});

const modalStyle = css({
    bottom: -1,
    borderRadius: 2,
    overflow: "unset",
    width: "100%",
    md: {
        bottom: "unset",
        maxWidth: "500px"
    }
});

const closeButtonStyle = css({
    cursor: "pointer",
    position: "absolute",
    top: -7,
    boxShadow: elevation(),
    md: {
        top: -3,
        right: -3,
        transform: "unset",
        boxShadow: elevation()
    }
});

const noAuthTextStyle = css({
    width: "100%",
    mt: 2,
    cursor: "pointer",
    maxHeight: "max-content"
});
